import type { FunnelCompany, FunnelStep, FunnelStepName } from './funnel-types';

/** One step to the next: how many went on, the conversion and the drop-off between them. */
export interface StepGap {
  from: FunnelStepName;
  to: FunnelStepName;
  /** Reached the earlier step. */
  entered: number;
  /** Reached the later one. */
  reached: number;
  /** Percent of the earlier step that reached the later, whole; null when nobody reached the earlier. */
  conversion: number | null;
  /** Reached the earlier step and not (yet) the later. */
  dropped: number;
  medianSeconds: number | null;
}

/**
 * Conversion and drop-off between every pair of neighbouring steps, from the counts the funnel
 * already carries. A step is counted on its own date, so a company can reach a later step without
 * an earlier one (pages sent through the API before any setup was finished); the conversion is held
 * at 100% and the drop-off at zero then, never "120%" or "-2 dropped".
 */
export function stepGaps(steps: readonly FunnelStep[]): StepGap[] {
  return steps.slice(1).map((step, i) => {
    const before = steps[i]!;
    const reached = Math.min(step.count, before.count);
    return {
      from: before.step,
      to: step.step,
      entered: before.count,
      reached: step.count,
      conversion: before.count > 0 ? Math.round((reached * 100) / before.count) : null,
      dropped: before.count - reached,
      medianSeconds: step.medianSeconds,
    };
  });
}

/** The last bucket of the activation histogram holds this many days and more. */
export const LONGEST_BUCKET = 14;

const DAY_MS = 86_400_000;

/** Whole days from sign-up to first export, for each company that has exported. */
export function daysToActivate(companies: readonly FunnelCompany[]): number[] {
  return companies.flatMap((c) => {
    if (!c.firstExportAt) return [];
    const ms = Date.parse(c.firstExportAt) - Date.parse(c.signedUpAt);
    return Number.isFinite(ms) ? [Math.max(0, Math.floor(ms / DAY_MS))] : [];
  });
}

/** Companies by days to first export: day 0 (the same day) to 13, then 14 and more in one bucket. */
export function activationBuckets(days: readonly number[]): number[] {
  const buckets = Array.from({ length: LONGEST_BUCKET + 1 }, () => 0);
  for (const d of days) buckets[Math.min(d, LONGEST_BUCKET)]! += 1;
  return buckets;
}

/**
 * The median time from sign-up to the first export, in seconds, over the companies that exported;
 * null for none. The First export step and the histogram both say this one number, so the two
 * medians on the board never disagree (round 3, marketing should 5).
 */
export function medianActivationSeconds(companies: readonly FunnelCompany[]): number | null {
  const seconds = companies.flatMap((c) => {
    if (!c.firstExportAt) return [];
    const ms = Date.parse(c.firstExportAt) - Date.parse(c.signedUpAt);
    return Number.isFinite(ms) ? [Math.max(0, ms / 1000)] : [];
  });
  return medianDays(seconds);
}

/** The median of whole days, or null for none. */
export function medianDays(days: readonly number[]): number | null {
  if (days.length === 0) return null;
  const sorted = [...days].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
}
